import { Check, ChevronDown, Search } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { FORM_TRIGGER_CLASSES, FormSelectArrow } from './formVariant';

export interface SearchSelectOption {
  value: string;
  label: string;
}

interface SearchSelectProps {
  options: SearchSelectOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  className?: string;
  // 'form' = the plain <input>/<select> look of the other form fields (see
  // formVariant.tsx), so the field sits in a form without standing out.
  // 'default' keeps the large rounded look.
  variant?: 'default' | 'form';
  // Sizing overrides for the trigger button (merged last, so they win).
  buttonClassName?: string;
  // Same for the dropdown panel: short options (an ID list, say) don't need the
  // default extra-wide popup.
  popupClassName?: string;
}

/**
 * A select with a search box, for option lists already loaded on the client
 * (e.g. the 100+ health care facilities on User Management). Every
 * whitespace-separated token of the keyword has to match the option's value or
 * label, so "rs permata" finds "5 - RS PERMATA BUNDA PURWODADI". Same look and
 * keyboard handling as CodeSearchSelect, which searches on the server instead.
 */
const SearchSelect = ({
  options,
  value,
  onChange,
  disabled = false,
  placeholder,
  name,
  className,
  variant = 'default',
  buttonClassName,
  popupClassName,
}: SearchSelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isForm = variant === 'form';

  const filtered = useMemo(() => {
    const tokens = keyword.toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return options;
    return options.filter((option) => {
      const haystack = `${option.value} ${option.label}`.toLowerCase();
      return tokens.every((token) => haystack.includes(token));
    });
  }, [options, keyword]);

  const selectedLabel = useMemo(
    () => options.find((option) => option.value === value)?.label ?? '',
    [options, value]
  );

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Closing drops the keyword: reopening on last time's search would show a
  // filtered list with no visible reason for it.
  useEffect(() => {
    if (!open) {
      setKeyword('');
      return;
    }
    setHighlight(0);
    searchRef.current?.focus();
  }, [open]);

  const activeIndex = Math.min(highlight, Math.max(filtered.length - 1, 0));

  const commit = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const last = filtered.length - 1;
      const next =
        event.key === 'ArrowDown' ? Math.min(activeIndex + 1, last) : Math.max(activeIndex - 1, 0);
      setHighlight(next);
      listRef.current?.children[next]?.scrollIntoView({ block: 'nearest' });
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const item = filtered[activeIndex];
      if (item) commit(item.value);
    }
  };

  return (
    <div ref={wrapperRef} className={cn('relative w-full', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((previous) => !previous);
        }}
        className={cn(
          'flex w-full items-center justify-between gap-2 text-left',
          isForm
            ? [FORM_TRIGGER_CLASSES, disabled ? 'cursor-default' : 'cursor-pointer']
            : [
                'h-[45px] px-3 text-[20px] rounded-[12px] border bg-[#F6F6F6] dark:bg-[#F3F6FE]/10',
                'border-[#C2D2E2] dark:border-[#37424D]',
                disabled ? 'cursor-default opacity-60' : 'cursor-pointer',
              ],
          buttonClassName
        )}
      >
        {/* A native <select> shows its "Please select..." option in the normal
            text colour, so the form variant does too. */}
        <span className={cn('truncate', !value && !isForm && 'opacity-60')}>
          {value ? selectedLabel || value : (placeholder ?? '')}
        </span>
        {isForm ? (
          <FormSelectArrow disabled={disabled} />
        ) : (
          <ChevronDown className="w-4 h-4 shrink-0 opacity-60" />
        )}
      </button>

      {/* The real form value, so this behaves like the <select> it replaces. */}
      <input type="hidden" name={name} value={value} />

      {open && (
        <div
          // Wider than the trigger on purpose: facility names are long and the
          // form column is narrow, so the list would otherwise truncate every row.
          className={cn(
            'absolute left-0 top-full z-50 mt-1 rounded-[12px] border shadow-lg',
            'min-w-full w-[560px] max-w-[90vw]',
            'bg-db-card border-[#C2D2E2] dark:border-[#37424D]',
            popupClassName
          )}
        >
          <div className="flex items-center gap-2 p-2 border-b border-[#C2D2E2] dark:border-[#37424D]">
            <Search className="w-4 h-4 shrink-0 opacity-60" />
            <input
              ref={searchRef}
              type="text"
              value={keyword}
              placeholder={t('cmn > search')}
              onChange={(event) => {
                setKeyword(event.target.value);
                setHighlight(0);
              }}
              onKeyDown={handleKeyDown}
              className="w-full bg-transparent outline-none text-[14px] font-normal"
            />
          </div>

          <div ref={listRef} className="max-h-[240px] overflow-y-auto">
            {filtered.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(event) => {
                  // mousedown, not click: the search input's blur would close
                  // the popup before a click ever landed.
                  event.preventDefault();
                  commit(option.value);
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2 text-left text-[14px] font-normal',
                  index === activeIndex && 'bg-[#F3F6FE] dark:bg-[#F3F6FE]/10'
                )}
              >
                <span className="truncate">{option.label}</span>
                {option.value === value && <Check className="w-4 h-4 ml-auto shrink-0" />}
              </button>
            ))}

            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-[14px] opacity-60">
                {t('msg > no data')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchSelect;
